import type { StaffRole, StaffRow } from '@mybusinessmate/schema';
import { ROLE_LABEL } from './staff-nav';

const MIN = 60_000;
// "1:40 PM" as the design writes it (en-IN would lower-case it).
const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const dayMonth = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' });

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** "Online now", "5 min ago", "1 h ago", "Today, 1:40 PM", "Yesterday", "3 Oct", or "Never signed in". */
export function lastActiveLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'Never signed in';
  const at = new Date(iso);
  const ago = now.getTime() - at.getTime();
  if (ago < 5 * MIN) return 'Online now';
  if (ago < 60 * MIN) return `${Math.floor(ago / MIN)} min ago`;
  if (ago < 3 * 60 * MIN) return `${Math.floor(ago / (60 * MIN))} h ago`;
  const days = Math.round((startOfDay(now) - startOfDay(at)) / 86_400_000);
  if (days <= 0) return `Today, ${time.format(at)}`;
  if (days === 1) return 'Yesterday';
  return dayMonth.format(at);
}

/** The role as the people on the team are called it (Figma A04): "Account manager". */
export const roleLabel = (role: StaffRole) => ROLE_LABEL[role];

/** "Growth lead · Management", or whatever of the two is set. */
export const roleSubtitle = (staff: Pick<StaffRow, 'title' | 'department'>) =>
  [staff.title, staff.department].filter(Boolean).join(' · ');

export interface Workload {
  /** What the row says: "4 of 5 tasks" or "12 clients · 9 primary". */
  text: string;
  /** 0-100 for the bar; null when there is no meaningful capacity to compare to (account managers). */
  percent: number | null;
  /** At or over capacity: shown in amber. */
  full: boolean;
}

/** Specialists and leads are measured against their task capacity; account managers by how many clients they hold. */
export function workloadOf(
  staff: Pick<StaffRow, 'staffRole' | 'activeTaskCount' | 'maxActiveTasks' | 'clientCount' | 'primaryClientCount'>,
): Workload {
  if (staff.staffRole === 'account_manager' || (staff.staffRole === 'admin' && staff.clientCount > 0)) {
    return {
      text: `${staff.clientCount} ${staff.clientCount === 1 ? 'client' : 'clients'} · ${staff.primaryClientCount} primary`,
      percent: null,
      full: false,
    };
  }
  if (staff.activeTaskCount === 0) return { text: 'No work yet', percent: 0, full: false };
  const percent = Math.min(100, Math.round((staff.activeTaskCount / Math.max(1, staff.maxActiveTasks)) * 100));
  const over = staff.activeTaskCount - staff.maxActiveTasks;
  return {
    text:
      over > 0
        ? `${staff.activeTaskCount} of ${staff.maxActiveTasks} tasks · over by ${over}`
        : `${staff.activeTaskCount} of ${staff.maxActiveTasks} tasks · ${percent}%`,
    percent,
    full: staff.activeTaskCount >= staff.maxActiveTasks,
  };
}

const ACTIVITY: Record<string, string> = {
  'project.created': 'Created a project',
  'task.created': 'Created a task',
  'task.draft_saved': 'Saved a task as a draft',
  'task.draft_updated': 'Updated a draft task',
  'task.submitted': 'Submitted a task',
  'business.account_manager_assigned': 'Assigned an account manager',
  'business.profile_created': 'Set up a business profile',
  'business.profile_updated': 'Updated a business profile',
  'staff.provisioned': 'Added someone to the team',
  'staff.role_changed': 'Changed someone’s role',
  'staff.profile_updated': 'Updated someone’s profile',
  'staff.suspended': 'Suspended someone’s access',
  'staff.reinstated': 'Reinstated someone’s access',
};

/** An audit action code as a sentence for the activity list. A code with no wording shows as it is, not as nothing. */
export const describeActivity = (action: string) => ACTIVITY[action] ?? action;
